import { ArrowRight, Sparkles, BedDouble, ScanLine, CheckCircle2, CalendarDays, FileSpreadsheet, Plus, Trash2 } from "lucide-react";

const ROOM_TYPES = ["singola", "doppia", "matrimoniale", "tripla", "suite", "dormitorio"];

/* ------------ Reusable sub-components ------------ */

function TourCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-stone-200 p-4 flex gap-3 bg-stone-50/40">
      <Icon className="w-7 h-7 text-blue-700 flex-shrink-0 mt-1" strokeWidth={1.5} />
      <div>
        <div className="font-medium text-stone-900 mb-1">{title}</div>
        <div className="text-sm text-stone-600 leading-snug">{body}</div>
      </div>
    </div>
  );
}

function RoomRow({ idx, room, onChange, onRemove, removable, t }) {
  return (
    <div
      data-testid={`onboarding-room-row-${idx}`}
      className="border border-stone-200 p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end bg-stone-50/40"
    >
      <label className="block">
        <span className="text-xs tracked text-stone-500 mb-1 block">{t("onboarding.room_name")}</span>
        <input
          data-testid={`onboarding-room-name-${idx}`}
          value={room.name}
          onChange={(e) => onChange(idx, { name: e.target.value })}
          placeholder={t("onboarding.room_name_ph")}
          className="w-full border-2 border-stone-300 px-3 py-2 text-base focus:outline-none focus:border-blue-700"
        />
      </label>
      <label className="block">
        <span className="text-xs tracked text-stone-500 mb-1 block">{t("onboarding.room_type")}</span>
        <select
          data-testid={`onboarding-room-type-${idx}`}
          value={room.type}
          onChange={(e) => onChange(idx, { type: e.target.value })}
          className="border-2 border-stone-300 px-3 py-2 text-base focus:outline-none focus:border-blue-700"
        >
          {ROOM_TYPES.map((rt) => (
            <option key={rt} value={rt}>{t(`onboarding.rt_${rt}`)}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="text-xs tracked text-stone-500 mb-1 block">{t("onboarding.room_cap")}</span>
        <input
          type="number" min={1} max={20}
          data-testid={`onboarding-room-cap-${idx}`}
          value={room.capacity}
          onChange={(e) => onChange(idx, { capacity: e.target.value })}
          className="w-20 border-2 border-stone-300 px-3 py-2 text-base focus:outline-none focus:border-blue-700"
        />
      </label>
      <label className="block">
        <span className="text-xs tracked text-stone-500 mb-1 block">{t("onboarding.room_price")}</span>
        <input
          type="number" min={0}
          data-testid={`onboarding-room-price-${idx}`}
          value={room.price_per_night}
          onChange={(e) => onChange(idx, { price_per_night: e.target.value })}
          className="w-24 border-2 border-stone-300 px-3 py-2 text-base focus:outline-none focus:border-blue-700"
        />
      </label>
      <button
        type="button"
        onClick={() => onRemove(idx)}
        disabled={!removable}
        aria-label={t("onboarding.room_remove")}
        data-testid={`onboarding-room-remove-${idx}`}
        className="text-stone-400 hover:text-red-700 disabled:opacity-30 p-2"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ------------ Step 1 — Welcome ------------ */

export function WelcomeStep({ t, userName, onNext, onSkip }) {
  return (
    <div className="px-8 pb-8" data-testid="onboarding-step-1">
      <Sparkles className="w-12 h-12 text-blue-700 mb-4" strokeWidth={1.5} />
      <h2 className="font-serif text-4xl tracking-tight mb-3">
        {t("onboarding.welcome_title", { name: userName })}
      </h2>
      <p className="text-stone-700 text-lg leading-relaxed mb-2">{t("onboarding.welcome_p1")}</p>
      <p className="text-stone-600 mb-8">{t("onboarding.welcome_p2")}</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onNext}
          data-testid="onboarding-next-1"
          className="bg-blue-700 text-white px-8 py-4 text-lg rounded-sm hover:bg-blue-700 inline-flex items-center justify-center gap-3"
        >
          {t("onboarding.start")} <ArrowRight className="w-5 h-5" />
        </button>
        <button
          onClick={onSkip}
          data-testid="onboarding-skip-1"
          className="text-stone-600 px-4 py-4 text-base hover:text-stone-900"
        >
          {t("onboarding.skip_for_now")}
        </button>
      </div>
    </div>
  );
}

/* ------------ Step 2 — Tour ------------ */

export function TourStep({ t, onBack, onNext }) {
  return (
    <div className="px-8 pb-8" data-testid="onboarding-step-2">
      <h2 className="font-serif text-3xl tracking-tight mb-2">{t("onboarding.tour_title")}</h2>
      <p className="text-stone-600 mb-6">{t("onboarding.tour_sub")}</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        <TourCard icon={ScanLine} title={t("onboarding.tour_scan_t")} body={t("onboarding.tour_scan_b")} />
        <TourCard icon={CalendarDays} title={t("onboarding.tour_cal_t")} body={t("onboarding.tour_cal_b")} />
        <TourCard icon={FileSpreadsheet} title={t("onboarding.tour_pol_t")} body={t("onboarding.tour_pol_b")} />
        <TourCard icon={BedDouble} title={t("onboarding.tour_rooms_t")} body={t("onboarding.tour_rooms_b")} />
      </div>
      <div className="flex gap-3">
        <button onClick={onBack} className="text-stone-600 px-4 py-4 text-base hover:text-stone-900">
          {t("onboarding.back")}
        </button>
        <button
          onClick={onNext}
          data-testid="onboarding-next-2"
          className="flex-1 bg-blue-700 text-white px-6 py-4 text-lg rounded-sm hover:bg-blue-700 inline-flex items-center justify-center gap-2"
        >
          {t("onboarding.continue")} <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

/* ------------ Step 3 — Rooms ------------ */

export function RoomsStep({ t, rooms, onUpdate, onAdd, onRemove, onBack, onSave, saving }) {
  return (
    <div className="px-8 pb-8" data-testid="onboarding-step-3">
      <BedDouble className="w-12 h-12 text-blue-700 mb-4" strokeWidth={1.5} />
      <h2 className="font-serif text-3xl tracking-tight mb-2">{t("onboarding.rooms_title")}</h2>
      <p className="text-stone-600 mb-6">{t("onboarding.rooms_sub")}</p>

      <div className="space-y-3 mb-4 max-h-[40vh] overflow-y-auto pr-2">
        {rooms.map((r, idx) => (
          <RoomRow
            key={r.id || `room-${idx}`}
            idx={idx}
            room={r}
            onChange={onUpdate}
            onRemove={onRemove}
            removable={rooms.length > 1}
            t={t}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onAdd}
        data-testid="onboarding-add-room"
        className="mb-6 inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-700"
      >
        <Plus className="w-4 h-4" /> {t("onboarding.add_another_room")}
      </button>

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={onBack} className="text-stone-600 px-4 py-4 text-base hover:text-stone-900">
          {t("onboarding.back")}
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="onboarding-save-rooms"
          className="flex-1 bg-blue-700 text-white px-6 py-4 text-lg rounded-sm hover:bg-blue-700 disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {saving ? t("onboarding.saving") : (
            <>{t("onboarding.save_rooms")} <ArrowRight className="w-5 h-5" /></>
          )}
        </button>
      </div>
    </div>
  );
}

/* ------------ Step 4 — Done ------------ */

export function DoneStep({ t, onScan, onCalendar, onHome }) {
  return (
    <div className="px-8 pb-8 text-center" data-testid="onboarding-step-4">
      <CheckCircle2 className="w-16 h-16 text-green-700 mb-4 mx-auto" strokeWidth={1.5} />
      <h2 className="font-serif text-3xl tracking-tight mb-3">{t("onboarding.done_title")}</h2>
      <p className="text-stone-700 text-lg mb-2">{t("onboarding.done_p1")}</p>
      <p className="text-stone-600 mb-8 text-sm">{t("onboarding.done_hint")}</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={onScan}
          data-testid="onboarding-cta-scan"
          className="bg-blue-700 text-white px-6 py-4 text-lg rounded-sm hover:bg-blue-700 inline-flex items-center justify-center gap-2"
        >
          <ScanLine className="w-5 h-5" /> {t("onboarding.cta_scan")}
        </button>
        <button
          onClick={onCalendar}
          data-testid="onboarding-cta-calendar"
          className="border-2 border-stone-300 text-stone-700 px-6 py-4 text-lg rounded-sm hover:bg-stone-50 inline-flex items-center justify-center gap-2"
        >
          <CalendarDays className="w-5 h-5" /> {t("onboarding.cta_calendar")}
        </button>
        <button
          onClick={onHome}
          data-testid="onboarding-cta-home"
          className="text-stone-600 px-4 py-4 text-base hover:text-stone-900"
        >
          {t("onboarding.cta_home")}
        </button>
      </div>
    </div>
  );
}

/* ------------ Step indicator (top bar) ------------ */

export function StepIndicator({ step, labels }) {
  return (
    <div className="px-8 pt-8 pb-4 flex flex-wrap items-center gap-2 text-xs tracked text-stone-500">
      {labels.map((label, i) => (
        <span key={`${label}-${i}`} className="flex items-center gap-2">
          <span className={step >= i + 1 ? "text-blue-700 font-medium" : ""}>
            {i + 1}. {label}
          </span>
          {i < labels.length - 1 && <ArrowRight className="w-3 h-3" />}
        </span>
      ))}
    </div>
  );
}
